<template>
  <div class="director-edit-form">
    <!-- Personal Information -->
    <div class="form-section">
      <div class="form-section-header">
        <v-icon size="16" class="mr-2">mdi-account-outline</v-icon>
        {{ tl('Personal Information') }}
      </div>
      <div class="form-section-body">
        <div class="form-grid">
          <v-select
            v-model="form.idType"
            :items="idTypeOptions"
            item-title="label"
            item-value="value"
            :label="tl('Identification Type')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
          />
          <v-text-field
            v-model="form.idNumber"
            :label="idNumberLabel"
            :placeholder="idNumberPlaceholder"
            :maxlength="idNumberMaxlength"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            class="col-span-2"
          />
          <v-text-field
            v-model="form.name"
            :label="tl('Full Name')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            class="col-span-3"
            @update:model-value="(v: string) => { form.name = (v || '').toUpperCase(); }"
          />
          <v-select
            v-model="form.designation"
            :items="designationOptions"
            item-title="label"
            item-value="value"
            :label="tl('Designation')"
            variant="outlined"
            density="compact"
            readonly
            hide-details="auto"
            class="field-readonly"
          />
          <v-text-field
            :model-value="formatDisplayDate(form.dob)"
            :label="tl('Date of Birth')"
            variant="outlined"
            density="compact"
            readonly
            hide-details="auto"
            class="field-readonly"
          />
          <v-select
            v-model="form.gender"
            :items="genderOptions"
            item-title="label"
            item-value="value"
            :label="tl('Gender')"
            variant="outlined"
            density="compact"
            readonly
            hide-details="auto"
            class="field-readonly"
          />
          <v-select
            v-model="form.race"
            :items="raceOptions"
            item-title="label"
            item-value="value"
            :label="tl('Race')"
            variant="outlined"
            density="compact"
            readonly
            hide-details="auto"
            class="field-readonly"
          />
          <v-select
            v-model="form.nationality"
            :items="nationalityOptions"
            item-title="label"
            item-value="value"
            :label="tl('Nationality')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
          />
          <v-select
            v-model="form.businessOccupation"
            :items="businessOccupationOptions"
            item-title="label"
            item-value="value"
            :label="tl('Business Occupation')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
          />
          <v-text-field
            v-model="form.email"
            :label="tl('Email')"
            data-required :rules="[rules.required, rules.email]"
            type="email"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
          />
        </div>
      </div>
    </div>

    <!-- Residential Address -->
    <div class="form-section">
      <div class="form-section-header">
        <v-icon size="16" class="mr-2">mdi-home-outline</v-icon>
        {{ tl('Residential Address') }}
      </div>
      <div class="form-section-body">
        <div class="form-grid">
          <v-text-field
            v-model="form.residentialStreet01"
            :label="tl('Street 01')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="(v: string) => { form.residentialStreet01 = (v || '').toUpperCase(); }"
          />
          <v-text-field
            v-model="form.residentialStreet02"
            :label="tl('Street 02')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="(v: string) => { form.residentialStreet02 = (v || '').toUpperCase(); }"
          />
          <v-text-field
            v-model="form.residentialStreet03"
            :label="tl('Street 03')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="(v: string) => { form.residentialStreet03 = (v || '').toUpperCase(); }"
          />
          <v-select
            v-model="form.residentialCountry"
            :items="nationalityOptions"
            item-title="label"
            item-value="value"
            :label="tl('Country')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
          />
          <!-- State: select for Malaysia, text for foreign -->
          <v-autocomplete
            v-if="isResidentialMalaysia"
            v-model="form.residentialState"
            :items="stateItems"
            :label="tl('State')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="onResidentialStateChange"
          />
          <v-text-field
            v-else
            v-model="form.residentialState"
            :label="tl('State / Province')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="(v: string) => { form.residentialState = (v || '').toUpperCase(); }"
          />
          <!-- City: autocomplete for Malaysia -->
          <v-autocomplete
            v-if="isResidentialMalaysia"
            v-model="form.residentialCity"
            :items="residentialCityItems"
            :label="tl('City')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="onResidentialCityChange"
          />
          <v-text-field
            v-else
            v-model="form.residentialCity"
            :label="tl('City')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="(v: string) => { form.residentialCity = (v || '').toUpperCase(); }"
          />
          <!-- Postcode: autocomplete for Malaysia -->
          <v-autocomplete
            v-if="isResidentialMalaysia"
            v-model="form.residentialPostcode"
            :items="residentialPostcodeItems"
            :label="tl('Post Code')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="onResidentialPostcodeChange"
          />
          <v-text-field
            v-else
            v-model="form.residentialPostcode"
            :label="tl('Post Code')"
            data-required :rules="[rules.required]"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
          />
        </div>
      </div>
    </div>

    <!-- Service Address -->
    <div class="form-section">
      <div class="form-section-header">
        <v-icon size="16" class="mr-2">mdi-office-building-outline</v-icon>
        {{ tl('Service Address') }}
      </div>
      <div class="form-section-body">
        <v-checkbox
          v-model="form.sameAsAbove"
          :label="tl('Same as Residential Address')"
          density="compact"
          :readonly="readOnly"
          hide-details="auto"
          class="mb-3"
        />
        <div v-if="!form.sameAsAbove" class="form-grid">
          <v-text-field
            v-model="form.serviceStreet01"
            :label="tl('Street 01')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="(v: string) => { form.serviceStreet01 = (v || '').toUpperCase(); }"
          />
          <v-text-field
            v-model="form.serviceStreet02"
            :label="tl('Street 02')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="(v: string) => { form.serviceStreet02 = (v || '').toUpperCase(); }"
          />
          <v-text-field
            v-model="form.serviceStreet03"
            :label="tl('Street 03')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="(v: string) => { form.serviceStreet03 = (v || '').toUpperCase(); }"
          />
          <v-select
            v-model="form.serviceCountry"
            :items="nationalityOptions"
            item-title="label"
            item-value="value"
            :label="tl('Country')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
          />
          <v-autocomplete
            v-if="isServiceMalaysia"
            v-model="form.serviceState"
            :items="stateItems"
            :label="tl('State')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="onServiceStateChange"
          />
          <v-text-field
            v-else
            v-model="form.serviceState"
            :label="tl('State / Province')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="(v: string) => { form.serviceState = (v || '').toUpperCase(); }"
          />
          <v-autocomplete
            v-if="isServiceMalaysia"
            v-model="form.serviceCity"
            :items="serviceCityItems"
            :label="tl('City')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="onServiceCityChange"
          />
          <v-text-field
            v-else
            v-model="form.serviceCity"
            :label="tl('City')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="(v: string) => { form.serviceCity = (v || '').toUpperCase(); }"
          />
          <v-autocomplete
            v-if="isServiceMalaysia"
            v-model="form.servicePostcode"
            :items="servicePostcodeItems"
            :label="tl('Post Code')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
            @update:model-value="onServicePostcodeChange"
          />
          <v-text-field
            v-else
            v-model="form.servicePostcode"
            :label="tl('Post Code')"
            variant="outlined"
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            bg-color="white"
          />
        </div>
      </div>
    </div>

    <!-- Attachment (Section A only) -->
    <div v-if="showAttachmentSection" class="form-section">
      <div class="form-section-header">
        <v-icon size="16" class="mr-2">mdi-paperclip</v-icon>
        {{ tl('Attachment') }}
      </div>
      <div class="form-section-body">
        <div class="form-grid-2">
          <div class="file-field">
            <label class="file-label">{{ tl('Name') }}</label>
            <v-file-input
              v-model="nameAttachment"
              :placeholder="tl('No file chosen')"
              variant="outlined"
              density="compact"
              :readonly="readOnly"
              :error-messages="nameAttachmentError"
              accept=".pdf,.jpg,.jpeg,.png"
              prepend-icon=""
              prepend-inner-icon="mdi-file-document-outline"
              bg-color="white"
              hide-details="auto"
              @update:model-value="onNameAttachmentChange"
            />
            <div class="text-caption text-medium-emphasis mt-1">.PDF, .JPG, .PNG ({{ tl('max 3MB') }})</div>
          </div>
          <div class="file-field">
            <label class="file-label">{{ tl('Identification') }}</label>
            <v-file-input
              v-model="identificationAttachment"
              :placeholder="tl('No file chosen')"
              variant="outlined"
              density="compact"
              :readonly="readOnly"
              :error-messages="identificationAttachmentError"
              accept=".pdf,.jpg,.jpeg,.png"
              prepend-icon=""
              prepend-inner-icon="mdi-card-account-details-outline"
              bg-color="white"
              hide-details="auto"
              @update:model-value="onIdentificationAttachmentChange"
            />
            <div class="text-caption text-medium-emphasis mt-1">.PDF, .JPG, .PNG ({{ tl('max 3MB') }})</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Other Directorship -->
    <div v-if="showAttachmentSection" class="form-section">
      <div class="form-section-header">
        <v-icon size="16" class="mr-2">mdi-briefcase-outline</v-icon>
        {{ tl('Other Directorship') }}
      </div>
      <div class="form-section-body">
        <div class="directorship-question">
          <span class="text-body-2 font-weight-medium">{{ tl('Does this director has any other directorship') }}</span>
          <v-radio-group
            v-model="form.hasOtherDirectorship"
            inline
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            class="mt-2"
          >
            <v-radio :label="tl('YES')" value="YES" color="primary" />
            <v-radio :label="tl('NO')" value="NO" color="primary" />
          </v-radio-group>
        </div>

        <!-- Directorship entry form (YES 선택 시) -->
        <template v-if="form.hasOtherDirectorship === 'YES'">
          <v-divider class="my-3" />

          <!-- Tabs: Registered Company / Group of Companies -->
          <v-tabs v-model="directorshipTab" density="compact" color="primary" class="mb-3">
            <v-tab value="registered">{{ tl('Registered Company') }}</v-tab>
            <v-tab value="group">{{ tl('Group of Companies/Others') }}</v-tab>
          </v-tabs>

          <v-window v-model="directorshipTab">
            <!-- Registered Company Tab -->
            <v-window-item value="registered">
              <div class="directorship-entry-form">
                <v-text-field
                  :model-value="directorshipEntry.companyName"
                  :label="tl('Company Name')"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  readonly
                  :placeholder="tl('Click to select entity via AI')"
                  style="cursor: pointer;"
                  bg-color="white"
                  class="mb-2"
                  @click="requestDirectorshipEntity"
                >
                  <template #append-inner>
                    <v-btn
                      icon="mdi-creation"
                      color="primary"
                      size="x-small"
                      variant="text"
                      density="comfortable"
                      @click.stop="requestDirectorshipEntity"
                    />
                  </template>
                </v-text-field>
                <div class="form-grid-2">
                  <v-text-field
                    v-model="directorshipEntry.companyNo"
                    :label="tl('Company No')"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    readonly
                    class="field-readonly"
                  />
                  <div />
                  <v-menu v-model="dirDateAppointedMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-text-field
                        v-bind="menuProps"
                        :model-value="formatDisplayDate(directorshipEntry.dateAppointed)"
                        :label="tl('Date Appointed')"
                        :placeholder="tl('DD MMM YYYY')"
                        readonly
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                        bg-color="white"
                      >
                        <template #append-inner><v-icon size="18">mdi-calendar</v-icon></template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :model-value="ymdToDate(directorshipEntry.dateAppointed)"
                      color="primary"
                      show-adjacent-months
                      @update:model-value="(v: any) => { directorshipEntry.dateAppointed = dateToYmd(v); dirDateAppointedMenu = false; }"
                    />
                  </v-menu>
                  <v-menu v-model="dirDateCeasedMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-text-field
                        v-bind="menuProps"
                        :model-value="formatDisplayDate(directorshipEntry.dateCeased)"
                        :label="tl('Ceased as Director')"
                        :placeholder="tl('DD MMM YYYY')"
                        readonly
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                        bg-color="white"
                      >
                        <template #append-inner><v-icon size="18">mdi-calendar</v-icon></template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :model-value="ymdToDate(directorshipEntry.dateCeased)"
                      color="primary"
                      show-adjacent-months
                      @update:model-value="(v: any) => { directorshipEntry.dateCeased = dateToYmd(v); dirDateCeasedMenu = false; }"
                    />
                  </v-menu>
                </div>
              </div>
            </v-window-item>

            <!-- Group of Companies/Others Tab -->
            <v-window-item value="group">
              <div class="directorship-entry-form">
                <div class="form-grid-2">
                  <v-text-field
                    v-model="directorshipEntry.companyName"
                    :label="tl('Company Name')"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    bg-color="white"
                    :readonly="readOnly"
                    @update:model-value="(v: string) => { directorshipEntry.companyName = (v || '').toUpperCase(); }"
                  />
                  <v-text-field
                    v-model="directorshipEntry.companyNo"
                    :label="tl('Company No')"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    bg-color="white"
                    :readonly="readOnly"
                  />
                  <v-menu v-model="dirDateAppointedMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-text-field
                        v-bind="menuProps"
                        :model-value="formatDisplayDate(directorshipEntry.dateAppointed)"
                        :label="tl('Date Appointed')"
                        :placeholder="tl('DD MMM YYYY')"
                        readonly
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                        bg-color="white"
                      >
                        <template #append-inner><v-icon size="18">mdi-calendar</v-icon></template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :model-value="ymdToDate(directorshipEntry.dateAppointed)"
                      color="primary"
                      show-adjacent-months
                      @update:model-value="(v: any) => { directorshipEntry.dateAppointed = dateToYmd(v); dirDateAppointedMenu = false; }"
                    />
                  </v-menu>
                  <v-menu v-model="dirDateCeasedMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-text-field
                        v-bind="menuProps"
                        :model-value="formatDisplayDate(directorshipEntry.dateCeased)"
                        :label="tl('Ceased as Director')"
                        :placeholder="tl('DD MMM YYYY')"
                        readonly
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                        bg-color="white"
                      >
                        <template #append-inner><v-icon size="18">mdi-calendar</v-icon></template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :model-value="ymdToDate(directorshipEntry.dateCeased)"
                      color="primary"
                      show-adjacent-months
                      @update:model-value="(v: any) => { directorshipEntry.dateCeased = dateToYmd(v); dirDateCeasedMenu = false; }"
                    />
                  </v-menu>
                </div>
              </div>
            </v-window-item>
          </v-window>

          <!-- Add button -->
          <div class="d-flex justify-end mt-3">
            <v-btn
              color="primary"
              variant="tonal"
              size="small"
              :disabled="!canAddDirectorship || readOnly"
              @click="addDirectorship"
            >
              <v-icon start size="16">mdi-plus</v-icon>
              {{ tl('Add Directorship') }}
            </v-btn>
          </div>

          <!-- Directorship list table -->
          <div v-if="otherDirectorships.length > 0" class="directorship-table mt-3">
            <div class="directorship-table-header">
              <div class="dth-no">#</div>
              <div class="dth-regNo">{{ tl('Company Reg No') }}</div>
              <div class="dth-name">{{ tl('Company Name') }}</div>
              <div class="dth-appointed">{{ tl('Date Appointed') }}</div>
              <div class="dth-ceased">{{ tl('Date Ceased') }}</div>
              <div class="dth-action"></div>
            </div>
            <div
              v-for="(item, idx) in otherDirectorships"
              :key="idx"
              class="directorship-table-row"
            >
              <div class="dth-no">{{ idx + 1 }}</div>
              <div class="dth-regNo">{{ item.companyNo || '-' }}</div>
              <div class="dth-name">{{ item.companyName || '-' }}</div>
              <div class="dth-appointed">{{ formatDisplayDate(item.dateAppointed) }}</div>
              <div class="dth-ceased">{{ formatDisplayDate(item.dateCeased) }}</div>
              <div class="dth-action">
                <v-btn
                  icon="mdi-delete-outline"
                  size="x-small"
                  variant="text"
                  color="error"
                  :disabled="readOnly"
                  @click="removeDirectorship(idx)"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, inject } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import type { LettagaDirector } from '../composables/useDirectorList';

// Translation
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

// Reference data
import idTypesData from '../../shared/data/idpro/id-types.json';
import gendersData from '../../shared/data/common/genders.json';
import racesData from '../../shared/data/common/races.json';
import nationalitiesData from '../../shared/data/common/nationalities.json';
import statesData from '../../shared/data/MBRS/states.json';
import citiesData from '../../shared/data/common/cities.json';
import postcodesData from '../../shared/data/common/postcodes.json';
import formConfigJson from '../on-form-config.json';
import { resolveNationalityValue } from '../utils/resolveNationality';

// Types
export interface DirectorshipEntry {
  companyNo: string;
  companyName: string;
  dateAppointed: string;
  dateCeased: string;
  source: 'registered' | 'group';
}

export interface DirectorFormData {
  idType: string;
  idNumber: string;
  name: string;
  designation: string;
  dob: string;
  gender: string;
  race: string;
  nationality: string;
  businessOccupation: string;
  email: string;
  residentialStreet01: string;
  residentialStreet02: string;
  residentialStreet03: string;
  residentialCountry: string;
  residentialState: string;
  residentialCity: string;
  residentialPostcode: string;
  sameAsAbove: boolean;
  serviceStreet01: string;
  serviceStreet02: string;
  serviceStreet03: string;
  serviceCountry: string;
  serviceState: string;
  serviceCity: string;
  servicePostcode: string;
  hasOtherDirectorship: string;
  otherDirectorships: DirectorshipEntry[];
  nameAttachment: File[] | null;
  identificationAttachment: File[] | null;
  _isDirty: boolean;
}

interface Props {
  director: LettagaDirector;
  readOnly?: boolean;
  sectionAction?: string;
}

const props = withDefaults(defineProps<Props>(), {
  readOnly: false,
  sectionAction: '',
});

const emit = defineEmits<{
  'update:formData': [data: DirectorFormData];
}>();

// Date display format: "12 MAY 2026"
function formatDisplayDate(dateStr: string): string {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return dateStr;
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${d} ${months[m - 1]} ${y}`;
}
function ymdToDate(ymd: string): Date | null {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function dateToYmd(d: Date | null): string {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Date picker menu refs
const dirDateAppointedMenu = ref(false);
const dirDateCeasedMenu = ref(false);

// Validation rules
const rules = {
  required: (v: any) => !!v || tl('This field is required'),
  email: (v: string) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || tl('Please enter a valid email'),
};

// Select options
const idTypeOptions = idTypesData as { value: string; label: string; desc?: string; placeholder?: string; maxlength?: number }[];
const genderOptions = gendersData as { value: string; label: string }[];
const raceOptions = racesData as { value: string; label: string }[];
const nationalityOptions = nationalitiesData as { value: string; label: string }[];
const designationOptions = (formConfigJson.enums.designations || []) as { value: string; label: string }[];
const businessOccupationOptions = (formConfigJson.enums.businessOccupations || []) as { value: string; label: string }[];

// Address data
const stateItems = (statesData as { value: string }[]).map(s => s.value);
const allCities = citiesData as { value: string; label: string; state: string }[];
const allPostcodes = postcodesData as { value: string; label: string; city: string; state: string }[];

// ID Number dynamic label
const idNumberLabel = computed(() => {
  const selected = idTypeOptions.find(t => t.value === form.idType);
  return selected?.label || tl('ID Number');
});
const idNumberPlaceholder = computed(() => {
  const selected = idTypeOptions.find(t => t.value === form.idType);
  return selected?.placeholder || '';
});
const idNumberMaxlength = computed(() => {
  const selected = idTypeOptions.find(t => t.value === form.idType);
  return selected?.maxlength || 50;
});

// Show attachment & other directorship only for Section A (change)
const showAttachmentSection = computed(() => props.sectionAction === 'change');

// File attachment refs (separate from reactive form for serialization)
const nameAttachment = ref<File[] | null>(null);
const identificationAttachment = ref<File[] | null>(null);
const nameAttachmentError = ref('');
const identificationAttachmentError = ref('');
const ATTACH_MAX_SIZE = 3 * 1024 * 1024; // 3MB
const ATTACH_ALLOWED_EXTS = ['.pdf', '.jpg', '.jpeg', '.png'];
const ATTACH_ALLOWED_MIMES = ['application/pdf', 'image/jpeg', 'image/png'];

function validateAttachment(files: File[] | null, errorRef: { value: string }): boolean {
  errorRef.value = '';
  if (!files || files.length === 0) return true;
  const file = files[0];
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  if (!ATTACH_ALLOWED_EXTS.includes(ext)) {
    errorRef.value = tl('Only .PDF, .JPG, .PNG files are allowed.');
    return false;
  }
  if (file.type && !ATTACH_ALLOWED_MIMES.includes(file.type)) {
    errorRef.value = tl('Only .PDF, .JPG, .PNG files are allowed.');
    return false;
  }
  if (file.size > ATTACH_MAX_SIZE) {
    errorRef.value = tl('File size must not exceed 3MB.');
    return false;
  }
  return true;
}

function onNameAttachmentChange(files: File[] | null) {
  if (!validateAttachment(files, nameAttachmentError)) {
    nameAttachment.value = null;
  }
  emitFormData();
}

function onIdentificationAttachmentChange(files: File[] | null) {
  if (!validateAttachment(files, identificationAttachmentError)) {
    identificationAttachment.value = null;
  }
  emitFormData();
}

// ── Other Directorship state ──
const pickDirectorshipEntity = inject<((callback: (entity: any) => void) => void) | null>('pickDirectorshipEntity', null);

function requestDirectorshipEntity() {
  if (props.readOnly) return;
  if (pickDirectorshipEntity) {
    pickDirectorshipEntity((entity: any) => {
      const regNo = (entity.registrationNo || entity.regNo || entity.REG_NO || '').toString().trim();
      const regNoOld = (entity.regNoOld || entity.REG_NO_OLD || '').toString().trim();
      const companyName = (entity.companyName || entity.entityName || entity.COMPANY_NAME || '').toString().trim();
      directorshipEntry.companyNo = [regNo, regNoOld].filter(Boolean).join(' / ');
      directorshipEntry.companyName = companyName;
    });
  }
}

const directorshipTab = ref('registered');
const otherDirectorships = ref<DirectorshipEntry[]>([]);
const directorshipEntry = reactive({
  companyNo: '',
  companyName: '',
  dateAppointed: '',
  dateCeased: '',
});

const canAddDirectorship = computed(() => {
  return directorshipEntry.companyNo.trim() || directorshipEntry.companyName.trim();
});

function clearDirectorshipEntry() {
  directorshipEntry.companyNo = '';
  directorshipEntry.companyName = '';
  directorshipEntry.dateAppointed = '';
  directorshipEntry.dateCeased = '';
}

function addDirectorship() {
  if (!canAddDirectorship.value) return;
  otherDirectorships.value.push({
    companyNo: directorshipEntry.companyNo.trim(),
    companyName: directorshipEntry.companyName.trim(),
    dateAppointed: directorshipEntry.dateAppointed,
    dateCeased: directorshipEntry.dateCeased,
    source: directorshipTab.value as 'registered' | 'group',
  });
  clearDirectorshipEntry();
  emitFormData();
}

function removeDirectorship(idx: number) {
  otherDirectorships.value.splice(idx, 1);
  emitFormData();
}

// Form state
const form = reactive({
  idType: '',
  idNumber: '',
  name: '',
  designation: '',
  dob: '',
  gender: '',
  race: '',
  nationality: '',
  businessOccupation: '',
  email: '',
  residentialStreet01: '',
  residentialStreet02: '',
  residentialStreet03: '',
  residentialCountry: 'MALAYSIA',
  residentialState: '',
  residentialCity: '',
  residentialPostcode: '',
  sameAsAbove: false,
  serviceStreet01: '',
  serviceStreet02: '',
  serviceStreet03: '',
  serviceCountry: 'MALAYSIA',
  serviceState: '',
  serviceCity: '',
  servicePostcode: '',
  hasOtherDirectorship: '',
  _isDirty: false,
});

// Original data snapshot for change detection
let originalSnapshot = '';

// Country checks
const isResidentialMalaysia = computed(() => form.residentialCountry === 'MALAYSIA');
const isServiceMalaysia = computed(() => form.serviceCountry === 'MALAYSIA');

// ── State key mapping (MBRS → common) ──
function toCommonStateKey(s: string): string {
  return s.replace(/\./g, '').replace(/\s+/g, '_');
}
function fromCommonStateKey(commonKey: string): string {
  return stateItems.find(s => toCommonStateKey(s) === commonKey) || commonKey;
}

// ── Cascading: Residential ──
const residentialCityItems = computed(() => {
  if (!form.residentialState) return allCities.map(c => c.label);
  return allCities.filter(c => c.state === toCommonStateKey(form.residentialState)).map(c => c.label);
});

const residentialPostcodeItems = computed(() => {
  let list = allPostcodes as typeof allPostcodes;
  if (form.residentialState) list = list.filter(p => p.state === toCommonStateKey(form.residentialState));
  if (form.residentialCity) {
    const city = allCities.find(c => c.label === form.residentialCity);
    if (city) list = list.filter(p => p.city === city.value);
  }
  return list.map(p => p.label);
});

function onResidentialStateChange() {
  form.residentialCity = '';
  form.residentialPostcode = '';
}

function onResidentialCityChange() {
  form.residentialPostcode = '';
  if (form.residentialCity) {
    const city = allCities.find(c => c.label === form.residentialCity);
    if (city) {
      const pcs = allPostcodes.filter(p => p.city === city.value);
      if (pcs.length === 1) form.residentialPostcode = pcs[0].label;
    }
  }
}

function onResidentialPostcodeChange() {
  if (!form.residentialPostcode) return;
  const matches = allPostcodes.filter(p => p.label === form.residentialPostcode);
  if (matches.length === 0) return;
  const states = [...new Set(matches.map(m => m.state))];
  if (states.length === 1) form.residentialState = fromCommonStateKey(states[0]);
  if (matches.length === 1) {
    const city = allCities.find(c => c.value === matches[0].city);
    if (city) form.residentialCity = city.label;
  }
}

// ── Cascading: Service ──
const serviceCityItems = computed(() => {
  if (!form.serviceState) return allCities.map(c => c.label);
  return allCities.filter(c => c.state === toCommonStateKey(form.serviceState)).map(c => c.label);
});

const servicePostcodeItems = computed(() => {
  let list = allPostcodes as typeof allPostcodes;
  if (form.serviceState) list = list.filter(p => p.state === toCommonStateKey(form.serviceState));
  if (form.serviceCity) {
    const city = allCities.find(c => c.label === form.serviceCity);
    if (city) list = list.filter(p => p.city === city.value);
  }
  return list.map(p => p.label);
});

function onServiceStateChange() {
  form.serviceCity = '';
  form.servicePostcode = '';
}

function onServiceCityChange() {
  form.servicePostcode = '';
  if (form.serviceCity) {
    const city = allCities.find(c => c.label === form.serviceCity);
    if (city) {
      const pcs = allPostcodes.filter(p => p.city === city.value);
      if (pcs.length === 1) form.servicePostcode = pcs[0].label;
    }
  }
}

function onServicePostcodeChange() {
  if (!form.servicePostcode) return;
  const matches = allPostcodes.filter(p => p.label === form.servicePostcode);
  if (matches.length === 0) return;
  const states = [...new Set(matches.map(m => m.state))];
  if (states.length === 1) form.serviceState = fromCommonStateKey(states[0]);
  if (matches.length === 1) {
    const city = allCities.find(c => c.value === matches[0].city);
    if (city) form.serviceCity = city.label;
  }
}

// ── Pre-fill from director data ──
function prefillFromDirector(d: LettagaDirector) {
  form.idType = detectIdType(d.contactRegNo);
  form.idNumber = d.contactRegNo || '';
  form.name = d.fullName || '';
  form.designation = mapPositionToDesignation(d.positionName);
  form.dob = '';
  form.gender = d.gender || '';
  form.race = d.race || '';
  form.nationality = resolveNationalityValue(d.nationalityDisplay || '', nationalityOptions);
  form.businessOccupation = d.designation || '';
  form.email = '';
  form.residentialStreet01 = '';
  form.residentialStreet02 = '';
  form.residentialStreet03 = '';
  form.residentialCountry = 'MALAYSIA';
  form.residentialState = '';
  form.residentialCity = '';
  form.residentialPostcode = '';
  form.sameAsAbove = false;
  form.serviceStreet01 = '';
  form.serviceStreet02 = '';
  form.serviceStreet03 = '';
  form.serviceCountry = 'MALAYSIA';
  form.serviceState = '';
  form.serviceCity = '';
  form.servicePostcode = '';
  form.hasOtherDirectorship = '';
  otherDirectorships.value = [];
  clearDirectorshipEntry();
  directorshipTab.value = 'registered';
  nameAttachment.value = null;
  identificationAttachment.value = null;
  nameAttachmentError.value = '';
  identificationAttachmentError.value = '';
  form._isDirty = false;
}

function detectIdType(regNo: string): string {
  if (!regNo) return '';
  const cleaned = regNo.replace(/[-\s]/g, '');
  if (/^\d{12}$/.test(cleaned)) return 'NRIC';
  if (/^[A-Z]/i.test(cleaned)) return 'PASSPORT';
  return '';
}

function mapPositionToDesignation(positionName: string): string {
  if (!positionName) return 'DIRECTOR';
  const upper = positionName.toUpperCase();
  if (upper.includes('ALTERNATE')) return 'ALT_DIRECTOR';
  return 'DIRECTOR';
}

// ── Change detection ──
function getFormSnapshot(): string {
  const { _isDirty, ...rest } = form;
  return JSON.stringify(rest);
}

function checkDirty() {
  form._isDirty = getFormSnapshot() !== originalSnapshot;
}

function buildEmitData(): DirectorFormData {
  return {
    ...form,
    otherDirectorships: [...otherDirectorships.value],
    nameAttachment: nameAttachment.value,
    identificationAttachment: identificationAttachment.value,
  };
}

function emitFormData() {
  checkDirty();
  emit('update:formData', buildEmitData());
}

// ── Watchers ──
watch(form, () => {
  emitFormData();
}, { deep: true });

// Country change resets state/city/postcode
watch(() => form.residentialCountry, () => {
  form.residentialState = '';
  form.residentialCity = '';
  form.residentialPostcode = '';
});

watch(() => form.serviceCountry, () => {
  form.serviceState = '';
  form.serviceCity = '';
  form.servicePostcode = '';
});

onMounted(() => {
  prefillFromDirector(props.director);
  // Take snapshot after prefill
  originalSnapshot = getFormSnapshot();
  form._isDirty = false;
  emit('update:formData', buildEmitData());
});
</script>

<style scoped>
.director-edit-form {
  padding: 4px 0;
}

/* ── Section container ── */
.form-section {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;
}

.form-section:last-child {
  margin-bottom: 0;
}

.form-section-header {
  display: flex;
  align-items: center;
  background: #eef2f7;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 0.82rem;
  color: #3d4856;
  border-bottom: 1px solid #dce3eb;
  letter-spacing: 0.01em;
}

.form-section-body {
  padding: 14px 14px 10px;
  background: #f9fafb;
}

/* ── Grid layout ── */
.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px 12px;
  align-items: start;
}

.col-span-2 {
  grid-column: span 2;
}

.col-span-3 {
  grid-column: 1 / -1;
}

/* ── 2-column grid for attachments ── */
.form-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 16px;
  align-items: start;
}

.file-field {
  display: flex;
  flex-direction: column;
}

.file-label {
  font-weight: 600;
  font-size: 0.78rem;
  color: #3d4856;
  margin-bottom: 6px;
}

/* ── Directorship question ── */
.directorship-question {
  padding: 4px 0;
}

.directorship-entry-form {
  padding: 8px 0;
}

/* ── Directorship list table ── */
.directorship-table {
  border: 1px solid #dce3eb;
  border-radius: 4px;
  overflow-x: auto;
}

.directorship-table-header {
  display: grid;
  grid-template-columns: 30px minmax(100px, 1.2fr) minmax(100px, 2fr) minmax(90px, 1fr) minmax(90px, 1fr) 36px;
  gap: 0;
  background: #3d4856;
  color: #fff;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.directorship-table-row {
  display: grid;
  grid-template-columns: 30px minmax(100px, 1.2fr) minmax(100px, 2fr) minmax(90px, 1fr) minmax(90px, 1fr) 36px;
  gap: 0;
  padding: 8px 12px;
  font-size: 0.8rem;
  border-bottom: 1px solid #eee;
  align-items: center;
}

.directorship-table-row:last-child {
  border-bottom: none;
}

.directorship-table-row:hover {
  background: #f5f8fb;
}

.dth-no {
  text-align: center;
}

.dth-regNo,
.dth-name,
.dth-appointed,
.dth-ceased {
  padding: 0 4px;
  word-break: break-word;
}

.dth-action {
  text-align: center;
}

/* ── Readonly fields (Designation, DOB, Gender, Race) ── */
.field-readonly {
  opacity: 0.75;
}

.field-readonly :deep(.v-field) {
  background: #f0f0f0 !important;
  border-color: #d5d5d5 !important;
}

.field-readonly :deep(.v-field__input),
.field-readonly :deep(.v-select__selection-text) {
  color: #666 !important;
}

/* ── Input field enhancements ── */
:deep(.v-field--variant-outlined .v-field__outline) {
  --v-field-border-opacity: 0.4;
}

:deep(.v-field--variant-outlined:hover .v-field__outline) {
  --v-field-border-opacity: 0.7;
}

:deep(.v-field--variant-outlined.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 1;
}

/* ── Required field indicator (red asterisk) ── */
:deep([data-required] .v-label)::before {
  content: '* ';
  color: #f44336;
  font-weight: 700;
}
</style>
